import React, { useEffect, useRef } from "react";
import Backbone from "backbone";
import AppErrors from "app/components/flow/designer/appErrors/appErrors";
import AppExceptions from "app/components/flow/designer/appErrors/appExceptions";
import metaStoreService from "core/services/metaStoreService/meta-store-service";
import localErrorsStorage from "app/components/flow/designer/appErrors/localErrorsStorage";
import appErrorsDataSource from "app/components/flow/designer/appErrors/appErrorsDataSource";
import api from "core/api/api";
import statusManagement from "src/status-management";
import { useNodeSearch } from "../../helpers/flow.hooks";

interface AppErrorsWrapperProps {
    app: any;
    appStatus?: string;
    loadLocal?: boolean;
    isAutomatedGroup?: boolean;
    moveToAppGroups?: () => void;
    onClose?: () => void;
}

const AppErrorsWrapper: React.FC<AppErrorsWrapperProps> = ({
    app,
    appStatus,
    loadLocal = false,
    isAutomatedGroup = false,
    moveToAppGroups,
    onClose
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const viewRef = useRef<any>(null);
    const handleNodeSearch = useNodeSearch();

    useEffect(() => {
        if (!containerRef.current || !app) {
            return;
        }

        const loadAndShowErrors = async () => {
            let errors: any[] = [];

            if (loadLocal) {
                // Load errors from local storage
                errors = localErrorsStorage.load(app);
            } else {
                // Load errors from server using the same logic as the original designer
                try {
                    const appName = app.getNameWithNamespace ? app.getNameWithNamespace() : `${app.nsName}.${app.name}`;
                    const results = await api.getFlowErrors(appName);

                    let compilationError: any = null;
                    const currentAppStatus = appStatus || app.flowStatus;

                    if (
                        statusManagement.isApplicationCreated(currentAppStatus) ||
                        statusManagement.isApplicationInvalid(currentAppStatus)
                    ) {
                        try {
                            await api.compile(appName);
                        } catch (error) {
                            compilationError = error;
                        }
                    }

                    errors = appErrorsDataSource.parseFlowErrors(results, app, true);

                    if (compilationError) {
                        errors.unshift(appErrorsDataSource.getCompilationError(app, compilationError, true));
                    }

                    if (errors.length > 0) {
                        localErrorsStorage.save(app, errors);
                    }
                } catch (error) {
                    console.error("Failed to load app errors from server:", error);
                    // Fallback to local storage
                    errors = localErrorsStorage.load(app);
                }
            }

            if (errors.length === 0) {
                console.log("No errors found");
                return;
            }

            // Create the model structure expected by AppErrors.View
            const model = new Backbone.Model();
            model.app = app;

            // Create the AppErrors view
            const appErrorsView = new AppErrors.View({
                model: model,
                appStatus: appStatus,
                collection: new AppExceptions.Collection(errors),
                isAutomatedGroup: isAutomatedGroup,
                moveToAppGroups: moveToAppGroups
            });

            // Listen to events from the view
            if (handleNodeSearch) {
                appErrorsView.on("show-node", handleNodeSearch);
            }

            appErrorsView.on("clear-and-reload", () => {
                localErrorsStorage.clear(app);
                appErrorsView.collection.reset();
            });

            // Render the view into the container
            containerRef.current!.replaceChildren(appErrorsView.render().el);
            appErrorsView.triggerMethod("show");

            // Store the view reference for cleanup
            viewRef.current = appErrorsView;
        };

        loadAndShowErrors();

        // Cleanup function
        return () => {
            if (viewRef.current) {
                viewRef.current.destroy();
                viewRef.current = null;
            }
        };
    }, [app, appStatus, loadLocal, isAutomatedGroup, moveToAppGroups, handleNodeSearch]);

    return (
        <div
            ref={containerRef}
            style={{
                height: "100%",
                width: "100%",
                overflow: "hidden"
            }}
        />
    );
};

export default AppErrorsWrapper;
